import { useRouter } from 'next/router'
import Comments from 'components/Comments'
import EmbeddedWidget from 'components/EmbeddedWidget'
import Head from 'next/head'
import { createSecureHeaders } from 'next-secure-headers'
import getContentSecurityPolicy from 'config/getContentSecurityPolicy'
import { GetServerSidePropsContext } from 'next'
import { fetchHostFromDatabase } from 'pages/api/hosts/[hostId]'
import useParentUrlRecording from 'hooks/useParentUrlRecording'
import { isDev } from 'utilities/url'
import InlineAdminAttacher from 'components/InlineAdminAttacher'

type Props = {
  origin?: string,
}

// This page uses the parent URL to look up the conversation
// (sibling component uses a passed in conversationId)
const EmbeddedCommentsPage = ({ origin }: Props) => {
  const router = useRouter()
  const { hostId } = router.query
  // console.log('EMBEDDED COMMENTS PAGE')
  const parentUrl = useParentUrlRecording({ hostId: hostId as string })
  const canonicalUrl = getCanonicalUrl(origin, parentUrl)

  if (!hostId || !parentUrl || !canonicalUrl) {
    return null
  }
  // console.log({ hostId, parentUrl, canonicalUrl })
  return <>
    <Head>
      <base target='_blank' />
      <link rel='canonical' href={ canonicalUrl } />
      <meta name="robots" content="noindex,indexifembedded" />
    </Head>
    <EmbeddedWidget hostId={hostId as string}>
      <Comments hostId={hostId as string} url={parentUrl} />
      <InlineAdminAttacher blogSlug={hostId as string} />
    </EmbeddedWidget>
  </>
}

const getCanonicalUrl = (origin?: string, parentUrl?: string) => {
  if (!origin || !parentUrl) {
    return undefined
  }
  const originUrl = new URL('https://' + origin)
  const clientUrl = new URL(parentUrl)
  const canonicalUrl = `${originUrl.protocol}//${originUrl.host}${clientUrl.pathname}`
  // console.log({ canonicalUrl, protocol: originUrl.protocol, host: originUrl.host, pathname: clientUrl.pathname })
  return canonicalUrl
}

export async function getServerSideProps(context: GetServerSidePropsContext) {
  const hostId = context?.params?.hostId as string
  const host = await fetchHostFromDatabase(hostId)
  // console.log('EMBEDDED CONVERSATION by HOSTID GET HOST', hostId, host?.origin)
  const origin = isDev
    ? host?.origin.replace('.com', '.test:9105')
    : host?.origin

  if (origin) {
    const secureHeaders = createSecureHeaders({
      contentSecurityPolicy: getContentSecurityPolicy(origin.replace('https://', '')),
    })
    const csp = secureHeaders.find(header => header.key === 'Content-Security-Policy')
    if (csp?.value) {
      context.res?.setHeader('Content-Security-Policy', csp.value)
    }
  }

  return {
    props: {
      origin,
    }
  }
}

export default EmbeddedCommentsPage
